import React, { useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity, Alert, Image, TextInput, Platform } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../constants/theme';
import { CUSTOMER_CARD_ENABLED } from '../constants/featureFlags';
import { useFoodStore, claveProducto } from '../store/useFoodStore';
import { useAppStore } from '../store/useAppStore';
import { bloquearSiInvitado } from '../services/modoInvitado';
import { callCreateFoodOrder, callValidateCoupon } from '../services/cloudFunctions';
import { firestoreService } from '../services/firestoreService';
import { storageService } from '../services/storageService';
import { useStripe } from '../hooks/useStripeHooks';
import { Button } from '../components/Button';
import { volver } from '../utils/volver';
import { mensajeDeError } from '../utils/mensajeDeError';
import { pesos } from '../utils/dinero';
import { envioConOfertas, descuentoDeOfertas, ofertasDe } from '../constants/promocionesDeNegocio';

/**
 * V22 — el comprobante del carrito se subía tal cual salía de la galería: una
 * foto de 12 MP a calidad 0.8 pasa del tope de 5 MB de storage.rules y el
 * pedido por transferencia no se podía hacer. Misma reducción que
 * `reducirComprobante` de order-receipt.tsx (ancho 1400 px, JPEG al 70%, sin
 * recortar para no perder la referencia ni el monto). Si falla, sube la original.
 */
async function reducirComprobante(uri: string): Promise<string> {
  try {
    const salida = await ImageManipulator.manipulateAsync(
      uri,
      [{ resize: { width: 1400 } }],
      { compress: 0.7, format: ImageManipulator.SaveFormat.JPEG }
    );
    return salida.uri;
  } catch (e: any) {
    console.warn('[cart] no se pudo redimensionar el comprobante, subo la original:', e?.message ?? e);
    return uri;
  }
}

// Opciones de propina (% del subtotal) — modelo cultural RD
const TIP_OPTIONS: Array<{ label: string; pct: number }> = [
  { label: 'Sin propina', pct: 0 },
  { label: '5%', pct: 0.05 },
  { label: '10%', pct: 0.10 },
  { label: '15%', pct: 0.15 },
];

export default function CartScreen() {
  const router = useRouter();
  // Selectores específicos: evita re-render del carrito por cambios no relacionados
  const cart = useFoodStore((s) => s.cart);
  const removeFromCart = useFoodStore((s) => s.removeFromCart);
  const getCartTotal = useFoodStore((s) => s.getCartTotal);
  const clearCart = useFoodStore((s) => s.clearCart);
  const cartRestaurantId = useFoodStore((s) => s.cartRestaurantId);
  const restaurants = useFoodStore((s) => s.restaurants);

  // ════════════════════════════════════════════════════════════════════
  // EL RESTAURANTE DEL CARRITO SALE DE FIRESTORE, NO DE LA LISTA EN MEMORIA.
  // ════════════════════════════════════════════════════════════════════
  //
  // ── LO QUE PASABA ──────────────────────────────────────────────────
  //
  // Era `restaurants.find(...)` contra `useFoodStore.restaurants` — la lista
  // que pinta la pestaña Comida. Si ese restaurante no estaba EN ESA LISTA,
  // `restaurant` quedaba `undefined` y el carrito se volvía un callejón sin
  // salida, con tres mentiras a la vez:
  //   · «Delivery  RD$0.00» en el resumen, y ese 0 entraba en el total.
  //   · El botón «Transfer.» desaparecía (`bankAccount` salía de ese objeto),
  //     así que quien no tiene tarjeta se quedaba sin forma de pagar.
  //   · Y al tocar Pagar: «El restaurante seleccionado ya no está disponible»,
  //     de un negocio que estaba abierto y recibiendo pedidos.
  //
  // ── Y NO ERA UN CASO RARO ────────────────────────────────────────
  //
  //   1. Entrar por un aviso. `restaurant/[id].tsx` lee el restaurante
  //      DIRECTO de Firestore justo para eso — lo dice su propio comentario:
  //      «soporta deep-link desde push notifications sin depender del listing
  //      previo». Podías agregar platos y nunca llegar a pagarlos.
  //   2. Cambiar de categoría. `(tabs)/food.tsx` hace
  //      `setRestaurants(liveRestaurants)` con el filtro puesto: la lista se
  //      REEMPLAZA. Agregabas de un pollo, tocabas «Pizza», y tu pollo ya no
  //      estaba en la lista.
  //   3. Tener más de 30 restaurantes. El listener es `pageSize: 30`.
  //
  // ── POR QUÉ EL DOCUMENTO Y NO LA LISTA ─────────────────────────────
  //
  // `createFoodOrder` compone el total con el documento de Firestore
  // (`authoritativeSubtotal + rest.deliveryFee + tipAmount`) y lo compara con
  // el que manda el carrito. Leyendo el mismo documento, el número que la
  // persona VE es el que el servidor va a COBRAR — y como el listener es vivo,
  // si el dueño cambia el envío mientras el carrito está abierto el resumen se
  // actualiza, en vez de reventar al confirmar.
  //
  // La lista sigue de respaldo: si la lectura falla (reglas, sin internet),
  // `listenToRestaurantById` avisa con `null` y se cae a lo de antes. Nunca
  // queda peor que hoy.
  const restauranteDelListado = restaurants.find(r => r.id === cartRestaurantId);
  const [restauranteDelServidor, setRestauranteDelServidor] = useState<any | null>(null);
  const [resolviendoRestaurante, setResolviendoRestaurante] = useState(false);

  useEffect(() => {
    if (!cartRestaurantId) {
      setRestauranteDelServidor(null);
      setResolviendoRestaurante(false);
      return;
    }
    let vivo = true;
    setResolviendoRestaurante(true);
    const unsub = firestoreService.listenToRestaurantById(cartRestaurantId, (data) => {
      if (!vivo) return;
      setRestauranteDelServidor(data);
      setResolviendoRestaurante(false);
    });
    return () => {
      vivo = false;
      unsub();
    };
  }, [cartRestaurantId]);

  const restaurant = restauranteDelServidor ?? restauranteDelListado;
  // Solo se puede estar «resolviendo» si no hay NADA que pintar todavía.
  const envioSinConfirmar = resolviendoRestaurante && !restaurant;

  // Solo se ofrece transferencia si el restaurante tiene una cuenta a la que
  // transferir. Ver la nota junto al botón "Transfer.".
  const tieneCuentaBancaria = !!(restaurant as any)?.bankAccount?.number;
  const subtotal = getCartTotal();

  // ════════════════════════════════════════════════════════════════════
  // LAS PROMOCIONES DEL NEGOCIO — ESTE CARRITO NO LAS MIRABA NUNCA.
  // ═══════════════════════════════════════════════════════════════════
  //
  // Al cliente le llegaba un push «¡Delivery GRATIS!» (functions/src/promos.ts
  // lo manda de verdad, a todos los de la zona), veía el banner en Comida,
  // entraba… y aquí se le cobraba el envío completo. Y el dueño veía «ACTIVO»
  // en su panel creyendo que estaba asumiendo ese costo.
  //
  // La regla vive en `constants/promocionesDeNegocio.ts`, espejo de la del
  // servidor. Dos promociones, y cada una entra por donde puede:
  //
  //  · DELIVERY GRATIS cambia el envío, y por tanto el total que se manda a
  //    confirmar. Se puede porque depende sólo del subtotal: el servidor
  //    calcula lo mismo y los dos números cuadran siempre.
  //
  //  · EL 50% DEL PRIMER PEDIDO se enseña, pero NO se resta del total que se
  //    envía: lo aplica el servidor, igual que un cupón. Si la consulta de
  //    «¿ya pidió aquí?» falla en el teléfono, se enseña sin descuento y el
  //    servidor cobra menos. Al revés —prometer y que no se cumpla— no pasa.
  const promosDelNegocio = (restaurant as any)?.activePromotions;
  const { envio: deliveryFee, envioGratisPorPromo } = envioConOfertas(
    Number(restaurant?.deliveryFee),
    subtotal,
    promosDelNegocio
  );

  const userFirebaseUid = useAppStore((s) => s.userFirebaseUid);
  const userName = useAppStore((s) => s.userName);
  const userPhone = useAppStore((s) => s.userPhone);
  const userRole = useAppStore((s) => s.userRole);
  const selectedDeliveryAddress = useAppStore((s) => s.selectedDeliveryAddress);
  const setSelectedDeliveryAddress = useAppStore((s) => s.setSelectedDeliveryAddress);

  // `null` = no se pudo averiguar, y eso no es «sí». Ver `yaPidioEnEsteNegocio`.
  const [yaPidioAqui, setYaPidioAqui] = useState<boolean | null>(null);
  useEffect(() => {
    if (!userFirebaseUid || !cartRestaurantId) {
      setYaPidioAqui(null);
      return;
    }
    let vivo = true;
    firestoreService
      .yaPidioEnEsteNegocio(userFirebaseUid, cartRestaurantId)
      .then((r) => {
        if (vivo) setYaPidioAqui(r);
      });
    return () => {
      vivo = false;
    };
  }, [userFirebaseUid, cartRestaurantId]);

  const { descuento: descuentoPromo, oferta: ofertaAplicada } = descuentoDeOfertas(
    subtotal,
    promosDelNegocio,
    yaPidioAqui === false
  );

  // Cuánto le falta para el envío gratis, con el mínimo que puso el negocio.
  // Se toma el mínimo MÁS BAJO de sus ofertas de envío: es el primero al que
  // va a llegar, y prometerle el más alto sería pedirle que gaste de más.
  const minimosDeEnvio = ofertasDe(promosDelNegocio)
    .filter((o) => o.type === 'DELIVERY_GRATIS')
    .map((o) => o.minimo ?? 0)
    .filter((m) => m > subtotal);
  const faltaParaEnvioGratis =
    minimosDeEnvio.length > 0 ? Math.min(...minimosDeEnvio) - subtotal : null;

  const [paymentMethod, setPaymentMethod] = useState<'CASH' | 'TRANSFER' | 'CARD'>('CASH');
  const [receiptImage, setReceiptImage] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [tipPctIdx, setTipPctIdx] = useState(1); // default 5%

  const tipAmount = Math.round(subtotal * TIP_OPTIONS[tipPctIdx].pct);

  // Cupón / promo code
  const [couponInput, setCouponInput] = useState('');
  const [couponApplied, setCouponApplied] = useState<{ code: string; discount: number } | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  const couponDiscount = couponApplied?.discount ?? 0;
  // El BRUTO es lo que el servidor va a recomponer y comparar
  // (`authoritativeSubtotal + tarifaEnvio + tipAmount`): es el número que se
  // manda en `total`, y por eso el descuento del primer pedido no va aquí.
  const bruto = subtotal + deliveryFee + tipAmount;
  // Y esto es lo que la persona va a pagar de verdad, con los dos descuentos.
  const total = Math.max(0, bruto - couponDiscount - descuentoPromo);

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setValidatingCoupon(true);
    try {
      const r = await callValidateCoupon({
        code: couponInput.trim().toUpperCase(),
        subtotal: bruto,
        kind: 'FOOD',
      });
      if (r.data.ok) {
        setCouponApplied({ code: couponInput.trim().toUpperCase(), discount: r.data.discountAmount });
        setCouponInput('');
        Alert.alert('Cupón aplicado', r.data.message);
      } else {
        Alert.alert('No se pudo aplicar', r.data.message);
      }
    } catch (e: any) {
      Alert.alert('Error', mensajeDeError(e, 'No pudimos validar el cupón. Revisa el código e intenta de nuevo.'));
    } finally {
      setValidatingCoupon(false);
    }
  };

  // Cargar la dirección default del user automáticamente si no hay una seleccionada
  useEffect(() => {
    if (!userFirebaseUid || selectedDeliveryAddress) return;
    const unsub = firestoreService.listenToUserAddresses(userFirebaseUid, (items) => {
      const def = items.find((a: any) => a.isDefault) ?? items[0];
      if (def && !selectedDeliveryAddress) {
        setSelectedDeliveryAddress({
          id: def.id,
          label: def.label,
          line1: def.line1,
          lat: def.lat ?? null,
          lng: def.lng ?? null,
        });
      }
    });
    return () => unsub();
  }, [userFirebaseUid, selectedDeliveryAddress, setSelectedDeliveryAddress]);

  // H41, auditoría 16-sep-2026 — EL CANDADO LLEGABA TARDE. `processing` solo se
  // encendía DENTRO del «Pagar» del diálogo, así que dos toques seguidos abrían
  // DOS «Confirmar Pedido»; en Android se encolan, y confirmar los dos creaba dos
  // pedidos reales (dos PIN, el restaurante cocina dos veces). Va en un `ref` y
  // no en estado porque dos toques en el mismo cuadro ven el estado viejo; el
  // ref cambia en el acto. Se suelta al cancelar/cerrar el diálogo y al terminar.
  const checkoutAbierto = useRef(false);
  const soltarCheckout = () => {
    checkoutAbierto.current = false;
  };

  const handleCheckout = () => {
    if (processing || checkoutAbierto.current) return;
    if (cart.length === 0) return;
    if (bloquearSiInvitado('hacer un pedido')) return;
    if (!userFirebaseUid) {
      Alert.alert("Atención", "Debes iniciar sesión para procesar un pedido.");
      return;
    }
    if (!cartRestaurantId) {
      Alert.alert("Error", "El restaurante seleccionado ya no está disponible.");
      return;
    }
    if (!restaurant) {
      // Antes este mismo aviso salía también mientras cargaba y siempre que el
      // restaurante no estuviera en la lista en memoria: le decía «ya no está
      // disponible» de un negocio abierto. Ahora se distingue.
      Alert.alert(
        resolviendoRestaurante ? 'Un segundo' : 'Error',
        resolviendoRestaurante
          ? 'Estamos cargando los datos del restaurante. Vuelve a tocar Pagar en un momento.'
          : 'El restaurante seleccionado ya no está disponible.'
      );
      return;
    }
    if (!selectedDeliveryAddress) {
      Alert.alert(
        'Dirección requerida',
        'Necesitamos saber dónde entregar. Agrega o elige una dirección.',
        [
          { text: 'Cancelar', style: 'cancel' },
          { text: 'Elegir dirección', onPress: () => router.push('/addresses?pick=1' as any) },
        ]
      );
      return;
    }
    if (paymentMethod === 'TRANSFER' && !receiptImage) {
      Alert.alert('Comprobante Requerido', 'Por favor, sube la foto del comprobante de transferencia para proceder con tu orden.');
      return;
    }

    const confirmMsg = paymentMethod === 'CARD'
      ? `Vas a pagar ${pesos(total)} con tarjeta a ${restaurant?.name}. Stripe procesa el pago directamente con el restaurante (Go Safely no toca tu dinero).`
      : `¿Deseas procesar tu pago de ${pesos(total)} a ${restaurant?.name}?`;

    checkoutAbierto.current = true;
    Alert.alert(
      'Confirmar Pedido',
      confirmMsg,
      [
        { text: 'Cancelar', style: 'cancel', onPress: soltarCheckout },
        {
          text: 'Pagar',
          onPress: async () => {
            setProcessing(true);
            try {
              // Si es TRANSFER, sube el comprobante al Storage privado antes de crear la orden
              let receiptUrl: string | null = null;
              if (paymentMethod === 'TRANSFER' && receiptImage) {
                try {
                  // Usamos un orderId temporal compuesto por uid + timestamp; el doc final
                  // guardará esta URL. uploadOrderReceipt acepta URI directo (putFile nativo).
                  const tmpOrderId = `pending_${Date.now()}`;
                  const uriAjustada = await reducirComprobante(receiptImage);
                  receiptUrl = await storageService.uploadOrderReceipt(
                    tmpOrderId,
                    userFirebaseUid,
                    uriAjustada
                  );
                } catch (e: any) {
                  console.warn('No se pudo subir el comprobante:', e?.message);
                  Alert.alert(
                    'Error subiendo comprobante',
                    'No pudimos subir tu comprobante. Verifica tu conexión e intenta de nuevo.'
                  );
                  setProcessing(false);
                  return;
                }
              }

              // 1) Crear orden en backend (PIN server-side + opcional clientSecret de Stripe)
              const resp = await callCreateFoodOrder({
                restaurantId: cartRestaurantId,
                restaurantOwnerId: cartRestaurantId,
                restaurantName: restaurant?.name,
                customerName: userName,
                customerPhone: userPhone,
                items: cart.map(item => `${item.quantity}x ${item.product.name}`).join(', '),
                // FIX AUDITORÍA (cupón doble) — Enviamos el total BRUTO (sin
                // restar el cupón) + el subtotal de comida. El servidor aplica
                // el cupón UNA vez y valida el subtotal contra el menú real.
                subtotal: subtotal,
                total: bruto, // el BRUTO: es lo que el servidor recompone y compara
                deliveryAddress: selectedDeliveryAddress.line1,
                deliveryLat: selectedDeliveryAddress.lat ?? null,
                deliveryLng: selectedDeliveryAddress.lng ?? null,
                tip: tipAmount,
                couponCode: couponApplied?.code ?? null,
                paymentMethod,
                receiptImage: receiptUrl,
              } as any);

              const pin = resp.data.securityPin;
              const clientSecret = resp.data.clientSecret;

              // 2) Si es pago con TARJETA, presentar el Stripe Payment Sheet
              if (paymentMethod === 'CARD') {
                if (!clientSecret) {
                  Alert.alert('Error', 'El restaurante no completó su configuración de pagos. Elige Efectivo o Transferencia.');
                  setProcessing(false);
                  return;
                }
                const initResult = await initPaymentSheet({
                  merchantDisplayName: restaurant?.name ?? 'Go Safely',
                  paymentIntentClientSecret: clientSecret,
                  allowsDelayedPaymentMethods: false,
                  defaultBillingDetails: { name: userName || undefined },
                });
                if (initResult.error) {
                  Alert.alert('No se completó el pago', mensajeDeError(initResult.error, 'No pudimos abrir el pago con tarjeta. Intenta de nuevo o elige Efectivo o Transferencia.'));
                  setProcessing(false);
                  return;
                }
                const payResult = await presentPaymentSheet();
                if (payResult.error) {
                  if (payResult.error.code !== 'Canceled') {
                    Alert.alert('Pago fallido', mensajeDeError(payResult.error, 'No se pudo cobrar la tarjeta. Revisa los datos o prueba con otro método de pago.'));
                  }
                  setProcessing(false);
                  return;
                }
                // Pago exitoso → continuar el flujo de pedido
              }

              // 3) Limpiar carrito + mostrar PIN al cliente
              clearCart();

              Alert.alert(
                '¡Pedido Confirmado!',
                `El restaurante está preparando tu comida.\n\nTU PIN DE SEGURIDAD: ${pin}\n\nOBLIGATORIO: Deberás dictarle este PIN al motorista al recibir tu pedido.`,
                [{ text: 'Entendido', onPress: () => router.replace('/(tabs)/food') }]
              );
            } catch (error: any) {
              console.error("Error al crear pedido:", error);
              const msg = mensajeDeError(error, 'No pudimos procesar tu pedido. Verifica tu conexión e inténtalo de nuevo.');
              Alert.alert("Error", msg);
            } finally {
              setProcessing(false);
              soltarCheckout();
            }
          }
        }
      ],
      // Android: si el diálogo se cierra con «Atrás» o tocando fuera, también
      // se suelta el candado (si no, el botón quedaría muerto).
      { cancelable: true, onDismiss: soltarCheckout }
    );
    // En web `Alert.alert` no dibuja nada (react-native-web lo deja vacío): no
    // hay diálogo que se pueda duplicar ni botón que suelte el candado, así que
    // se suelta aquí mismo y la web se queda exactamente como estaba.
    if (Platform.OS === 'web') soltarCheckout();
  };

  // Stripe Payment Sheet — solo se inicializa cuando paymentMethod === 'CARD'
  const { initPaymentSheet, presentPaymentSheet } = useStripe();

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => volver(router)} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={Colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Mi Carrito</Text>
        <View style={{ width: 24 }} />
      </View>

      {cart.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons name="cart-outline" size={80} color={Colors.border} />
          <Text style={styles.emptyText}>Tu carrito está vacío</Text>
          <Button title="Explorar Restaurantes" onPress={() => router.replace('/(tabs)/food')} style={{ width: 200, marginTop: 20 }} />
        </View>
      ) : (
        <>
          <ScrollView showsVerticalScrollIndicator={false}>
            <View style={styles.restaurantInfo}>
              <Text style={styles.restaurantName}>Pedido de: {restaurant?.name}</Text>
            </View>

            <View style={styles.itemsContainer}>
              {/* `key` e identificación por `claveProducto`: con menús viejos
                  sin `id`, todos los platos compartían la key `undefined` —
                  React los trataba como el mismo nodo y el basurero borraba
                  el carrito entero. */}
              {cart.map((item) => (
                <View key={claveProducto(item.product)} style={styles.cartItem}>
                  <View style={styles.qtyBox}>
                    <Text style={styles.qtyText}>{item.quantity}x</Text>
                  </View>
                  <View style={styles.itemInfo}>
                    <Text style={styles.itemName}>{item.product.name}</Text>
                    <Text style={styles.itemPrice}>{pesos(item.product.price * item.quantity)}</Text>
                  </View>
                  <TouchableOpacity onPress={() => removeFromCart(claveProducto(item.product))}>
                    <Ionicons name="trash-outline" size={20} color="#FF3B30" />
                  </TouchableOpacity>
                </View>
              ))}
            </View>

            <View style={styles.addressContainer}>
              <View style={styles.addressHeader}>
                <Text style={styles.sectionTitle}>Dirección de Entrega</Text>
                <TouchableOpacity onPress={() => router.push('/addresses?pick=1' as any)}>
                  <Text style={styles.editText}>
                    {selectedDeliveryAddress ? 'Cambiar' : 'Elegir'}
                  </Text>
                </TouchableOpacity>
              </View>
              {selectedDeliveryAddress ? (
                <TouchableOpacity
                  style={styles.addressBox}
                  onPress={() => router.push('/addresses?pick=1' as any)}
                >
                  <Ionicons name="location" size={24} color={Colors.primary} />
                  <View style={styles.addressTextContainer}>
                    <Text style={styles.addressTitle}>{selectedDeliveryAddress.label}</Text>
                    <Text style={styles.addressSubtitle}>{selectedDeliveryAddress.line1}</Text>
                  </View>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={[styles.addressBox, { borderWidth: 1, borderStyle: 'dashed', borderColor: Colors.primary, backgroundColor: '#F0F4FF' }]}
                  onPress={() => router.push('/addresses?pick=1' as any)}
                >
                  <Ionicons name="add-circle" size={28} color={Colors.primary} />
                  <View style={styles.addressTextContainer}>
                    <Text style={[styles.addressTitle, { color: Colors.primary }]}>Agregar dirección</Text>
                    <Text style={styles.addressSubtitle}>Necesitamos saber dónde entregar</Text>
                  </View>
                </TouchableOpacity>
              )}
            </View>

            {/* PROPINA */}
            <View style={styles.addressContainer}>
              <View style={styles.addressHeader}>
                <Text style={styles.sectionTitle}>Propina al repartidor</Text>
                {tipAmount > 0 && (
                  <Text style={{ color: Colors.primary, fontWeight: '700' }}>{pesos(tipAmount)}</Text>
                )}
              </View>
              <Text style={{ fontSize: 12, color: Colors.textSecondary, marginBottom: 10 }}>
                100% va al repartidor. Go Safely no toma comisión.
              </Text>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                {TIP_OPTIONS.map((opt, idx) => (
                  <TouchableOpacity
                    key={opt.label}
                    style={[styles.tipBtn, tipPctIdx === idx && styles.tipBtnActive]}
                    onPress={() => setTipPctIdx(idx)}
                  >
                    <Text
                      style={[
                        styles.tipBtnText,
                        tipPctIdx === idx && { color: Colors.primary, fontWeight: '700' },
                      ]}
                    >
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.addressContainer}>
              <View style={styles.addressHeader}>
                <Text style={styles.sectionTitle}>Método de Pago</Text>
              </View>

              <View style={{ flexDirection: 'row', gap: 8, marginBottom: 15 }}>
                <TouchableOpacity
                  style={[styles.paymentBtn, paymentMethod === 'CASH' && styles.paymentBtnActive]}
                  onPress={() => setPaymentMethod('CASH')}
                >
                  <Ionicons name="cash" size={22} color={paymentMethod === 'CASH' ? Colors.primary : Colors.textSecondary} />
                  <Text style={[styles.paymentBtnText, paymentMethod === 'CASH' && { color: Colors.primary }]}>Efectivo</Text>
                </TouchableOpacity>

                {/* Pago de comida por tarjeta: fuera por ahora (efectivo o
                    transferencia). Controlado por CUSTOMER_CARD_ENABLED. */}
                {CUSTOMER_CARD_ENABLED && (
                  <TouchableOpacity
                    style={[styles.paymentBtn, paymentMethod === 'CARD' && styles.paymentBtnActive]}
                    onPress={() => setPaymentMethod('CARD')}
                  >
                    <Ionicons name="card" size={22} color={paymentMethod === 'CARD' ? Colors.primary : Colors.textSecondary} />
                    <Text style={[styles.paymentBtnText, paymentMethod === 'CARD' && { color: Colors.primary }]}>Tarjeta</Text>
                  </TouchableOpacity>
                )}

                {/* TRANSFERENCIA — solo si el restaurante tiene cuenta.
                    Antes se ofrecía siempre, y al elegirla salía "Banco no
                    informado - Cuenta no informada": se le pedía al cliente
                    que transfiriera dinero sin decirle a dónde. El campo
                    `bankAccount` no lo escribe nada en todo el proyecto y el
                    registro de restaurante ni lo pide, así que le pasaba al
                    100% de los pedidos. Si no hay cuenta, el método no existe. */}
                {tieneCuentaBancaria && (
                  <TouchableOpacity
                    style={[styles.paymentBtn, paymentMethod === 'TRANSFER' && styles.paymentBtnActive]}
                    onPress={() => setPaymentMethod('TRANSFER')}
                  >
                    <Ionicons name="business" size={22} color={paymentMethod === 'TRANSFER' ? Colors.primary : Colors.textSecondary} />
                    <Text style={[styles.paymentBtnText, paymentMethod === 'TRANSFER' && { color: Colors.primary }]}>Transfer.</Text>
                  </TouchableOpacity>
                )}
              </View>

              {paymentMethod === 'CARD' && (
                <View style={[styles.transferInfoBox, { backgroundColor: '#F0F8FF', borderColor: '#B0D4FF' }]}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                    <Ionicons name="shield-checkmark" size={20} color="#0066CC" style={{ marginRight: 8 }} />
                    <Text style={{ fontWeight: 'bold', color: '#0066CC' }}>Pago seguro con Stripe</Text>
                  </View>
                  <Text style={{ color: Colors.textSecondary, fontSize: 13, lineHeight: 18 }}>
                    El cargo se procesa directamente a {restaurant?.name}. Go Safely no toca tu dinero — somos solo el puente.
                  </Text>
                  {restaurant && !(restaurant as any).stripeChargesEnabled && (
                    <View style={{ marginTop: 10, padding: 10, backgroundColor: '#FFF5E1', borderRadius: 8, borderWidth: 1, borderColor: '#FFB84D' }}>
                      <Text style={{ color: '#A36500', fontSize: 12 }}>
                        ⚠️ Este restaurante aún no completó su configuración de pagos con tarjeta. Si el pago falla, elige Efectivo o Transferencia.
                      </Text>
                    </View>
                  )}
                </View>
              )}

              {paymentMethod === 'TRANSFER' && tieneCuentaBancaria && (
                <View style={styles.transferInfoBox}>
                  <Text style={{ fontWeight: 'bold', marginBottom: 5 }}>Cuenta del Restaurante:</Text>
                  <Text style={{ color: Colors.textSecondary, marginBottom: 2 }}>
                    {(restaurant as any)?.bankAccount?.bank} - {(restaurant as any)?.bankAccount?.number}
                  </Text>
                  <Text style={{ color: Colors.textSecondary, marginBottom: 15 }}>A nombre de: {restaurant?.name}</Text>

                  <TouchableOpacity style={styles.uploadBtn} onPress={async () => {
                    let result = await ImagePicker.launchImageLibraryAsync({
                      mediaTypes: ImagePicker.MediaTypeOptions.Images,
                      quality: 0.8,
                    });
                    if (!result.canceled) {
                      setReceiptImage(result.assets[0].uri);
                    }
                  }}>
                    <Ionicons name="cloud-upload-outline" size={24} color={Colors.primary} />
                    <Text style={{ marginLeft: 10, color: Colors.primary, fontWeight: 'bold' }}>
                      {receiptImage ? 'Cambiar Comprobante' : 'Subir Comprobante'}
                    </Text>
                  </TouchableOpacity>

                  {receiptImage && (
                    <View style={{ marginTop: 15, alignItems: 'center' }}>
                      <Image source={{ uri: receiptImage }} style={{ width: 100, height: 100, borderRadius: 10, borderWidth: 1, borderColor: Colors.border }} />
                      <Text style={{ color: '#4CAF50', marginTop: 5, fontSize: 12, fontWeight: 'bold' }}>Comprobante Adjuntado</Text>
                    </View>
                  )}
                </View>
              )}
            </View>

            {/* CUPÓN / PROMO CODE */}
            <View style={styles.addressContainer}>
              <View style={styles.addressHeader}>
                <Text style={styles.sectionTitle}>Cupón de descuento</Text>
                {couponApplied && (
                  <TouchableOpacity onPress={() => setCouponApplied(null)}>
                    <Text style={[styles.editText, { color: '#FF3B30' }]}>Quitar</Text>
                  </TouchableOpacity>
                )}
              </View>
              {couponApplied ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#E8F5E9', padding: 12, borderRadius: 10 }}>
                  <Ionicons name="pricetag" size={20} color="#00A86B" />
                  <View style={{ marginLeft: 10, flex: 1 }}>
                    <Text style={{ fontWeight: '700', color: '#1B5E20', fontSize: 14 }}>
                      {couponApplied.code}
                    </Text>
                    <Text style={{ fontSize: 12, color: '#2E7D32', marginTop: 2 }}>
                      Descuento de {pesos(couponApplied.discount)} aplicado
                    </Text>
                  </View>
                </View>
              ) : (
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: '#FAFAFA', borderRadius: 10, paddingHorizontal: 12, borderWidth: 1, borderColor: Colors.border }}>
                    <Ionicons name="pricetag-outline" size={18} color={Colors.textSecondary} />
                    <TextInput
                      style={{ flex: 1, marginLeft: 8, fontSize: 14, color: Colors.text, paddingVertical: 10 }}
                      placeholder="Código (ej. BIENVENIDO10)"
                      autoCapitalize="characters"
                      value={couponInput}
                      onChangeText={setCouponInput}
                      maxLength={30}
                    />
                  </View>
                  <TouchableOpacity
                    onPress={handleApplyCoupon}
                    disabled={!couponInput.trim() || validatingCoupon}
                    style={{
                      backgroundColor: couponInput.trim() ? Colors.primary : '#CCC',
                      paddingHorizontal: 18,
                      paddingVertical: 10,
                      borderRadius: 10,
                      justifyContent: 'center',
                    }}
                  >
                    <Text style={{ color: '#FFF', fontWeight: '700', fontSize: 13 }}>
                      {validatingCoupon ? '…' : 'Aplicar'}
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>

            <View style={styles.summaryContainer}>
              <Text style={styles.sectionTitle}>Resumen</Text>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Subtotal</Text>
                <Text style={styles.summaryValue}>{pesos(subtotal)}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>
                  Delivery{envioGratisPorPromo ? ' (gratis por el negocio)' : ''}
                </Text>
                {/* Un «RD$0.00» mientras carga es una promesa de envío gratis
                    que no se va a cumplir. Mejor no decir nada todavía. */}
                <Text
                  style={[
                    styles.summaryValue,
                    envioGratisPorPromo && { color: '#00A86B', fontWeight: '700' },
                  ]}
                >
                  {envioSinConfirmar ? 'Calculando…' : pesos(deliveryFee)}
                </Text>
              </View>
              {/* Al que le falta poco para el envío gratis se le dice, en vez de
                  dejarlo pagarlo sin saber que lo tenía a RD$30 de distancia.
                  El mínimo es el que puso el negocio en SU oferta. */}
              {!envioGratisPorPromo && deliveryFee > 0 && faltaParaEnvioGratis !== null && (
                <Text style={{ fontSize: 12, color: Colors.textSecondary, marginTop: -6, marginBottom: 8 }}>
                  Te falta {pesos(faltaParaEnvioGratis)} para que el delivery te salga gratis.
                </Text>
              )}
              {descuentoPromo > 0 && (
                <View style={styles.summaryRow}>
                  {/* El nombre que el negocio le puso a su oferta. Un descuento
                      sin nombre no se puede reclamar si algo sale mal. */}
                  <Text style={[styles.summaryLabel, { color: '#00A86B' }]}>
                    {ofertaAplicada?.title || 'Oferta del negocio'}
                  </Text>
                  <Text style={[styles.summaryValue, { color: '#00A86B', fontWeight: '700' }]}>
                    − {pesos(descuentoPromo)}
                  </Text>
                </View>
              )}
              {tipAmount > 0 && (
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Propina ({TIP_OPTIONS[tipPctIdx].label})</Text>
                  <Text style={styles.summaryValue}>{pesos(tipAmount)}</Text>
                </View>
              )}
              {couponDiscount > 0 && couponApplied && (
                <View style={styles.summaryRow}>
                  <Text style={[styles.summaryLabel, { color: '#00A86B' }]}>
                    Cupón {couponApplied.code}
                  </Text>
                  <Text style={[styles.summaryValue, { color: '#00A86B', fontWeight: '700' }]}>
                    − {pesos(couponDiscount)}
                  </Text>
                </View>
              )}
              <View style={[styles.summaryRow, styles.totalRow]}>
                <Text style={styles.totalLabel}>Total</Text>
                <Text style={styles.totalValue}>{pesos(total)}</Text>
              </View>
            </View>
          </ScrollView>

          <View style={styles.footer}>
            <Button
              title={
                processing
                  ? 'Procesando…'
                  : paymentMethod === 'CARD'
                  ? `Pagar ${pesos(total)} con tarjeta`
                  : paymentMethod === 'TRANSFER'
                  ? `Confirmar pedido por transferencia`
                  : `Pagar ${pesos(total)} en efectivo`
              }
              onPress={handleCheckout}
              loading={processing}
              disabled={processing}
            />
          </View>
        </>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#F8F9FA' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: SIZES.padding, backgroundColor: '#FFF' },
  backButton: { padding: 5 },
  headerTitle: { fontSize: 18, fontWeight: 'bold', color: Colors.text },

  emptyContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: SIZES.padding },
  emptyText: { fontSize: 18, color: Colors.textSecondary, marginTop: 15, fontWeight: '500' },

  restaurantInfo: { padding: SIZES.padding, backgroundColor: '#FFF', marginBottom: 10 },
  restaurantName: { fontSize: 16, fontWeight: 'bold', color: Colors.textSecondary },

  itemsContainer: { backgroundColor: '#FFF', paddingHorizontal: SIZES.padding, marginBottom: 10 },
  cartItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: 15, borderBottomWidth: 1, borderBottomColor: '#F0F0F0' },
  qtyBox: { backgroundColor: '#F0F4FF', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6, marginRight: 15 },
  qtyText: { color: Colors.primary, fontWeight: 'bold', fontSize: 14 },
  itemInfo: { flex: 1 },
  itemName: { fontSize: 15, fontWeight: '500', color: Colors.text, marginBottom: 4 },
  itemPrice: { fontSize: 14, color: Colors.textSecondary },

  addressContainer: { backgroundColor: '#FFF', padding: SIZES.padding, marginBottom: 10 },
  sectionTitle: { fontSize: 18, fontWeight: 'bold', color: Colors.text },
  addressHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 },
  editText: { color: Colors.primary, fontWeight: '600' },
  addressBox: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F9F9F9', padding: 15, borderRadius: 12 },
  addressTextContainer: { marginLeft: 15, flex: 1 },
  addressTitle: { fontSize: 16, fontWeight: 'bold', color: Colors.text },
  addressSubtitle: { fontSize: 13, color: Colors.textSecondary, marginTop: 2 },

  tipBtn: { flex: 1, paddingVertical: 12, borderRadius: 10, borderWidth: 1, borderColor: '#E0E0E0', alignItems: 'center', backgroundColor: '#FFF' },
  tipBtnActive: { borderColor: Colors.primary, backgroundColor: '#F0F4FF' },
  tipBtnText: { fontSize: 13, color: Colors.textSecondary, fontWeight: '500' },

  summaryContainer: { backgroundColor: '#FFF', padding: SIZES.padding, paddingBottom: 40 },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 8 },
  summaryLabel: { fontSize: 15, color: Colors.textSecondary },
  summaryValue: { fontSize: 15, color: Colors.text, fontWeight: '500' },
  totalRow: { borderTopWidth: 1, borderTopColor: '#EEE', marginTop: 10, paddingTop: 15 },
  totalLabel: { fontSize: 18, fontWeight: 'bold', color: Colors.text },
  totalValue: { fontSize: 18, fontWeight: 'bold', color: Colors.primary },

  footer: { backgroundColor: '#FFF', padding: SIZES.padding, paddingBottom: 30, borderTopWidth: 1, borderTopColor: '#EEE' },

  paymentBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', padding: 12, borderRadius: 12, borderWidth: 1, borderColor: '#E0E0E0' },
  paymentBtnActive: { borderColor: Colors.primary, backgroundColor: 'rgba(0, 102, 255, 0.05)' },
  paymentBtnText: { marginLeft: 8, fontWeight: 'bold', color: Colors.textSecondary },
  transferInfoBox: { backgroundColor: '#F9F9F9', padding: 15, borderRadius: 12, borderWidth: 1, borderColor: '#EEE' },
  uploadBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', padding: 12, borderRadius: 12, backgroundColor: 'rgba(0, 102, 255, 0.1)', borderStyle: 'dashed', borderWidth: 1, borderColor: Colors.primary }
});
